import React, { useEffect, useState } from "react";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import { FullScreenLoader } from "../Components/Loader";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FiGift } from "react-icons/fi";
import SearchBar from "../Components/SearchBar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import axiosInstance from "../../api/axiosInstance";
import { useSelector } from "react-redux";

const CustomerGiftsSchemes = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const [schemesData, setSchemesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSchemes, setExpandedSchemes] = useState(new Set());
  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    if (userInfo?.access_token) {
      fetchGiftsBySchemes();
    }
  }, [userInfo?.access_token]);

  const fetchGiftsBySchemes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get("/api/get-gift-masters/", {
        headers: {
          Authorization: `Bearer ${userInfo.access_token}`,
        },
        params: {
          is_live: true, // Only show live gifts
        },
      });

      if (response.data.status === 200) {
        // The API returns data grouped by scheme when no scheme_name is provided
        const data = response.data.data;
        if (Array.isArray(data)) {
          setSchemesData(data);
        } else {
          // If data is an object, convert it to array format
          const schemesArray = Object.keys(data).map((schemeName) => ({
            scheme_name: schemeName,
            gifts: Array.isArray(data[schemeName]) ? data[schemeName] : [],
            gift_count: Array.isArray(data[schemeName]) ? data[schemeName].length : 0,
          }));
          setSchemesData(schemesArray);
        }
      } else {
        setError(response.data.message || "Failed to fetch gift schemes");
      }
    } catch (err) {
      console.error("Error fetching gift schemes:", err);
      setError(err.response?.data?.message || "An error occurred while fetching gift schemes");
    } finally {
      setLoading(false);
    }
  };

  const toggleSchemeExpansion = (schemeName) => {
    setExpandedSchemes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(schemeName)) {
        newSet.delete(schemeName);
      } else {
        newSet.add(schemeName);
      }
      return newSet;
    });
  };

  // Filter schemes based on search and date
  const filteredSchemes = Array.isArray(schemesData) ? schemesData.filter((scheme) => {
    // Search filter
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase();
      const schemeName = scheme.scheme_name?.toLowerCase() || "";
      const createdBy = scheme.created_by?.toLowerCase() || "";
      if (!schemeName.includes(search) && !createdBy.includes(search)) {
        return false;
      }
    }

    // Date filter
    if (startDate || endDate) {
      if (scheme.created_at) {
        const createdDate = dayjs(scheme.created_at);
        if (startDate && createdDate.isBefore(dayjs(startDate).startOf("day"))) {
          return false;
        }
        if (endDate && createdDate.isAfter(dayjs(endDate).endOf("day"))) {
          return false;
        }
      }
    }

    return true;
  }) : [];

  return (
    <DashboardLayout>
      <FullScreenLoader 
        show={loading} 
        message="Loading gift schemes..." 
        variant="primary"
        blurIntensity="md"
      />

      <div className="max-w-full mx-auto px-2 sm:px-4 py-3 sm:py-4 md:py-6">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <button
            onClick={() => navigate(-1)}
            className="hover:text-gray-800 p-1 sm:p-2"
            aria-label="Go back"
          >
            <FaArrowLeft size={18} className="sm:w-5 sm:h-5" />
          </button>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-600">
            Gift Schemes
          </h1>
        </div>

        {/* Search and Filter Section */}
        <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
          <SearchBar
            value={searchValue}
            onChange={setSearchValue}
            placeholder="Search by Scheme Name / Created By"
          />
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <DatePicker
              selectsRange
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={setDateRange}
              isClearable
              placeholderText="Select date range"
              dateFormat="dd MMM yyyy"
              className="w-full sm:w-auto px-3 py-2 border-2 border-amber-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <p className="text-red-700 text-xs sm:text-sm">
              <strong>Error:</strong> {error?.message || error || "Failed to load gift schemes"}
            </p>
          </div>
        )}

        {/* Schemes List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            <p className="mt-2 text-gray-600 text-sm sm:text-base">Loading gift schemes...</p>
          </div>
        ) : filteredSchemes.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <FiGift className="mx-auto text-gray-400 text-4xl sm:text-5xl mb-4" />
            <p className="text-gray-600 text-base sm:text-lg font-semibold">No gift schemes available</p>
            <p className="text-gray-500 text-xs sm:text-sm mt-2">
              {searchValue || startDate || endDate
                ? "Try adjusting your search or filters"
                : "Check back later for exciting gift schemes!"}
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {filteredSchemes.map((scheme) => {
              const isExpanded = expandedSchemes.has(scheme.scheme_name);
              const gifts = Array.isArray(scheme.gifts) ? scheme.gifts : [];

              return (
                <div
                  key={scheme.scheme_id || scheme.scheme_name}
                  className="bg-white rounded-lg sm:rounded-xl shadow-md border border-gray-200 overflow-hidden"
                >
                  {/* Scheme Header - Mobile Responsive */}
                  <div
                    className="p-3 sm:p-4 cursor-pointer hover:bg-amber-50 transition-colors"
                    onClick={() => toggleSchemeExpansion(scheme.scheme_name)}
                  >
                    <div className="flex items-center justify-between gap-2 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1 sm:mb-2 truncate">
                          {scheme.scheme_name || "Untitled Scheme"}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                          <span className="inline-flex items-center px-2 sm:px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                            {scheme.gift_count || 0} Gift{scheme.gift_count !== 1 ? "s" : ""}
                          </span>
                          {scheme.created_by && (
                            <span className="truncate">
                              <span className="font-medium">By:</span> {scheme.created_by}
                            </span>
                          )}
                          {scheme.created_at && (
                            <span>
                              <span className="font-medium">Date:</span> {dayjs(scheme.created_at).format("DD MMM YYYY")}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        className="flex-shrink-0 p-1 sm:p-2 text-amber-600 hover:bg-amber-100 rounded transition-colors"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? (
                          <FaChevronUp size={18} className="sm:w-5 sm:h-5" />
                        ) : (
                          <FaChevronDown size={18} className="sm:w-5 sm:h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Gifts Section */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-3 sm:p-4 md:p-6 bg-gray-50">
                      <h4 className="text-sm sm:text-base font-bold text-gray-700 mb-3 sm:mb-4">
                        Gifts in this scheme ({gifts.length})
                      </h4>
                      
                      {gifts.length === 0 ? (
                        <p className="text-xs sm:text-sm text-gray-500 text-center py-4">
                          No gifts available in this scheme
                        </p>
                      ) : (
                        <>
                          {/* Desktop Table View */}
                          <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full bg-white rounded-lg border border-gray-200">
                              <thead className="bg-amber-500 text-white">
                                <tr>
                                  <th className="px-4 py-3 text-left text-sm font-semibold">Gift Image</th>
                                  <th className="px-4 py-3 text-left text-sm font-semibold">Gift Name</th>
                                  <th className="px-4 py-3 text-center text-sm font-semibold">QR Count</th>
                                  <th className="px-4 py-3 text-center text-sm font-semibold">Bumper Offer</th>
                                  <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                                  <th className="px-4 py-3 text-left text-sm font-semibold">Expired At</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {gifts.map((gift) => (
                                  <tr key={gift.gift_id} className="hover:bg-amber-50 transition-colors">
                                    <td className="px-4 py-3">
                                      {gift.gift_image ? (
                                        <img
                                          src={gift.gift_image}
                                          alt={gift.gift_name}
                                          className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded border border-gray-200"
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                            e.target.nextSibling.style.display = "flex";
                                          }}
                                        />
                                      ) : null}
                                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-xs text-gray-400" style={{ display: gift.gift_image ? "none" : "flex" }}>
                                        No Image
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                      {gift.gift_name || "-"}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {gift.qr_count || 0}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                        gift.is_additional_gift
                                          ? "bg-yellow-100 text-yellow-800"
                                          : "bg-gray-100 text-gray-600"
                                      }`}>
                                        {gift.is_additional_gift ? "Yes" : "No"}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                        gift.is_live
                                          ? "bg-green-100 text-green-800"
                                          : "bg-gray-100 text-gray-600"
                                      }`}>
                                        {gift.is_live ? "Live" : "Inactive"}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {gift.expired_at
                                        ? dayjs(gift.expired_at).format("DD MMM YYYY")
                                        : "-"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Mobile Card View */}
                          <div className="lg:hidden space-y-3">
                            {gifts.map((gift) => (
                              <div
                                key={gift.gift_id}
                                className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4"
                              >
                                <div className="flex gap-3 sm:gap-4">
                                  {/* Gift Image */}
                                  <div className="flex-shrink-0">
                                    {gift.gift_image ? (
                                      <img
                                        src={gift.gift_image}
                                        alt={gift.gift_name}
                                        className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded border border-gray-200"
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                          e.target.nextSibling.style.display = "flex";
                                        }}
                                      />
                                    ) : null}
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-[10px] sm:text-xs text-gray-400" style={{ display: gift.gift_image ? "none" : "flex" }}>
                                      No Image
                                    </div>
                                  </div>

                                  {/* Gift Details */}
                                  <div className="flex-1 min-w-0">
                                    <h5 className="text-sm sm:text-base font-bold text-gray-900 mb-2 truncate">
                                      {gift.gift_name || "-"}
                                    </h5>
                                    <div className="flex flex-wrap gap-2">
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        QR: {gift.qr_count || 0}
                                      </span>
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        gift.is_additional_gift
                                          ? "bg-yellow-100 text-yellow-800"
                                          : "bg-gray-100 text-gray-600"
                                      }`}>
                                        Bumper: {gift.is_additional_gift ? "Yes" : "No"}
                                      </span>
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        gift.is_live
                                          ? "bg-green-100 text-green-800"
                                          : "bg-gray-100 text-gray-600"
                                      }`}>
                                        {gift.is_live ? "Live" : "Inactive"}
                                      </span>
                                    </div>
                                    {gift.expired_at && (
                                      <p className="text-xs text-gray-600 mt-2">
                                        Expires: {dayjs(gift.expired_at).format("DD MMM YYYY")}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Results Count */}
        {!loading && filteredSchemes.length > 0 && (
          <div className="mt-4 sm:mt-6 text-center text-gray-600 text-xs sm:text-sm">
            Showing {filteredSchemes.length} scheme{filteredSchemes.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CustomerGiftsSchemes;

